import DOMPurify from 'dompurify'
export {
  tokenizeWikiText,
  wikiToPlainText,
  plainToWikiTextMappings,
  breakdownWikiTextBlocks,
  segmentVisualLists,
  decodeHTMLEntities,
  bracketsRegex
}

const bracketsRegex = /\[\[ *([^|<>\]]+?) *\| *([^\][]+?) *\]\]/gi
const linkRegex = /^https?:\/\/[^|<> ]+\.[a-z]{2,4}(\/[^|<> ]*)*/i
const externalLinkRegex =
  /\[\[ *(https?:\/\/[^|<> ]+\.[a-z]{2,4}(\/[^|<> ]*)*) *\| *([^\][]+[^\] ]) *\]\]/gi

// Helper function to decode HTML entities (×, ÷, /, etc.)
function decodeHTMLEntities(text: string): string {
  const textarea = document.createElement('textarea')
  textarea.innerHTML = text
  return textarea.value
}

function wikiToPlainText(wikiText: string): string {
  /* eslint-disable quotes */
  let plainText = wikiText.replace(bracketsRegex, '$2')
  plainText = plainText.replace(externalLinkRegex, '$3')

  // handle fractions that use a combination of <sup> and <sub> tags
  plainText = plainText.replace(
    /<sup>([a-zA-Z0-9]+)<\/sup>&frasl;<sub>([a-zA-Z0-9]+)<\/sub>/g,
    '$1/$2'
  )

  // handle exponents using <sup> tag
  plainText = plainText.replace(/<sup>([a-zA-Z0-9]+)<\/sup>/g, '^$1')

  plainText = decodeHTMLEntities(plainText)

  plainText = DOMPurify.sanitize(plainText)

  return plainText.trim()
}

function plainToWikiTextMappings(wikiText: string): Record<string, string>{
  let match: RegExpExecArray | null = null
  const mappings: Record<string, string> = {}

  bracketsRegex.lastIndex = 0

  while ((match = bracketsRegex.exec(wikiText)) !== null) {
    const plainText = match[2].trim()
    const mappedText = match[0].trim()

    mappings[plainText] = mappedText
  }

  return mappings
}

type StringOrObject = string | object

interface Strategy {
  title: string
  uuid: string
  image_url: string
  description: string
  slug: string
  linkQueryParams?: Record<string, string|object>
}
interface Resource {
  title: string
  uuid: string
  thumbnail_url: string
  description: string
  download_links?: StringOrObject[]
}


function tokenizeWikiText(wikiText: string, strategies: Record<string, Strategy> = {}, resources: Record<string, Resource> = {}): StringOrObject[] {
  const textSegments = []
  let copyTest = wikiText
  let match = null

  // reset lastIndex before searching
  // since we're working with a singleton regex object with the g flag, it maintains a lastIndex property
  // when we're searching, we want to assume the regex object is a "freshly created" one
  bracketsRegex.lastIndex = 0

  while ((match = bracketsRegex.exec(wikiText)) !== null) {
    const i = copyTest.indexOf(match[0])
    const seg = copyTest.substr(0, i)
    textSegments.push(seg)

    const inlineText = match[2].trim()
    const strategyNameOrLinkOrResourceUuids = match[1].trim()
    const resourceUuids = strategyNameOrLinkOrResourceUuids.split(',')

    // see if there's a strategy or resource or link, if yes, we inject an object, otherwise we just inject the inline text
    const strategy = strategies[strategyNameOrLinkOrResourceUuids.toLowerCase()]
    const resource = resources[resourceUuids[0].toLowerCase()]
    if (strategy) {
      textSegments.push({
        type: 'strategy',
        inlineText: inlineText,
        title: strategy.title,
        id: strategy.uuid,
        image_url: strategy.image_url,
        description: strategy.description.slice(0, 240) + '...',
        slug: strategy.slug,
        linkQueryParams: strategy.linkQueryParams || {}
      })
    } else if (resource) {
      textSegments.push({
        type: 'resource',
        resource: resource,
        inlineText: inlineText,
        title: resource.title,
        id: resource.uuid,
        image_url: resource.thumbnail_url,
        description: resource.description.slice(0, 240) + '...',
        download_links: resource.download_links
      })
    } else if (linkRegex.test(strategyNameOrLinkOrResourceUuids)) {
      textSegments.push({
        type: 'link',
        inlineText: inlineText,
        href: strategyNameOrLinkOrResourceUuids
      })
    } else {
      textSegments.push(inlineText.trim())
    }

    copyTest = copyTest.substr(i + match[0].length)
  }
  textSegments.push(copyTest)
  return textSegments
}

export function getAssessmentUUIDsFromWikiText(text: string): string[] {
  if (!text) return [];
  const matches = [...text.matchAll(/\[\[\s*(.*?)\s*\|\s*(.*?)\s*\]\]/g)]
  const uuids = matches.map(match => match[1].trim())

  return uuids
}

interface WikiTextBlock {
  wikiText: string
  markdownOnly: boolean
}

// given wiki text, break it down into "wiki text blocks" where each block
function breakdownWikiTextBlocks(wikiText: string): WikiTextBlock[] {
  if (!wikiText) return []
  const paragraphBlocks = wikiText.split('\n\n')
  return paragraphBlocks.map(toWikiTextBlock)
}

function toWikiTextBlock(wikiText: string): WikiTextBlock {
  bracketsRegex.lastIndex = 0
  return { wikiText, markdownOnly: !bracketsRegex.test(wikiText) }
}

type VisualListSegment = ({ type: 'text' } & WikiTextBlock) | { type: 'list', items: WikiTextBlock[] }

// Some authored content writes lists with a literal bullet glyph and a trailing <br>
// instead of markdown list syntax, e.g.
//
//   **Example Exercises:**<br>
//   • Curls using a stretch band<br>
//   • Leg lifts<br>
//
// Rendered as-is that is a visual list with no list semantics, which fails WCAG 1.3.1
// (axe rule `semantic-list`). Only glyph bullets are recognised here — markdown `- `/`* `
// lists already render as real <ul>s, and '·' is left alone because content uses it as an
// inline multiplication operator (`f(n) = c · f(n-1)`), never as a bullet.
const bulletGlyphs = '•‣▪◦∙'
const bulletLineRegex = new RegExp(`^\\s*[${bulletGlyphs}]\\s*(.*?)\\s*(?:<br\\s*/?>)?\\s*$`)
const trailingBreakRegex = /\s*<br\s*\/?>\s*$/
const breakBeforeBulletRegex = new RegExp(`<br\\s*/?>[^\\S\\n]*(?=[${bulletGlyphs}])`, 'g')

// Given the text of a single wiki text block, split it into text segments and runs of
// bullet-glyph list items so the caller can render the runs as real lists. Returns null
// when the block contains no bullet run, so untouched content keeps its exact markup.
function segmentVisualLists(blockText: string | null): VisualListSegment[] | null {
  if (!blockText) return null

  // bullets are usually separated by "<br>\n", but tolerate a bare "<br>" between them
  const lines = blockText.replace(breakBeforeBulletRegex, '\n').split('\n')

  const segments: VisualListSegment[] = []
  let textLines: string[] = []
  let items: WikiTextBlock[] = []

  const flushText = () => {
    if (!textLines.length) return
    const wikiText = textLines.join('\n')
    textLines = []
    // a run of blank lines around a list would only render as an empty paragraph
    if (!wikiText.trim()) return
    segments.push({ type: 'text', ...toWikiTextBlock(wikiText) })
  }
  const flushList = () => {
    if (!items.length) return
    segments.push({ type: 'list', items })
    items = []
  }

  lines.forEach(line => {
    const match = line.match(bulletLineRegex)
    if (match) {
      // a <br> at the end of the text before a list is just the list's leading line break
      while (textLines.length && !textLines[textLines.length - 1].trim()) textLines.pop()
      if (textLines.length) {
        textLines[textLines.length - 1] = textLines[textLines.length - 1].replace(trailingBreakRegex, '')
      }
      flushText()
      items.push(toWikiTextBlock(match[1]))
    } else {
      flushList()
      textLines.push(line)
    }
  })
  flushText()
  flushList()

  if (!segments.some(segment => segment.type === 'list')) return null
  return segments
}
